import type { Optional } from '@festina-life/core'
import { Big } from 'big.js'
import { numericFormatter, removeNumericFormat } from 'react-number-format'

import { getNumberFormatOptions } from '../numberInput/useNumberFormatOptions'

export const DEFAULT_PERCENT_DECIMAL_SCALE = 8
export const PERCENT_SUFFIX = ' %'

export const convertPercentage = (
  value: number | string,
  decimalScale: number,
): number => new Big(value).mul(100).round(decimalScale).toNumber()

export const deconvertPercentage = (
  value: number | string,
  decimalScale: number,
): number => {
  const convertedValue = new Big(value).div(100)
  return convertedValue.round(decimalScale).toNumber()
}

export const formatPercentage = ({
  value,
  locale,
  decimals,
}: {
  value: Optional<number | string>
  locale: string
  decimals: number
}): string =>
  value === undefined || value === null || value === ''
    ? '-'
    : numericFormatter(convertPercentage(value, decimals).toString(), {
        ...getNumberFormatOptions(locale, decimals),
        fixedDecimalScale: Number(value) === 0,
        suffix: PERCENT_SUFFIX,
      })

export const parsePercentage = ({
  value,
  locale,
  decimals,
}: {
  value: unknown
  locale: string
  decimals: number
}): number => {
  if (typeof value !== 'string' || !value.trim().endsWith('%')) {
    return NaN
  }

  const options = getNumberFormatOptions(locale)
  const percentage = value.trim().slice(0, -1).trim()
  const normalized = percentage
    .replaceAll(options.thousandSeparator ?? '', '')
    .replaceAll(options.decimalSeparator ?? '.', '.')

  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    return NaN
  }

  const numericValue = removeNumericFormat(
    percentage,
    {
      from: { start: 0, end: 0 },
      to: { start: 0, end: percentage.length },
      lastValue: '',
    },
    { ...options, allowNegative: true },
  )
  return Number.isFinite(Number(numericValue))
    ? deconvertPercentage(numericValue, decimals)
    : NaN
}
